/**
 * Composable for User Notifications
 * Provides methods to get translated notification content and action URLs
 */

import { useI18n } from 'vue-i18n';
import { getNotificationRoute } from '@/services/notificationRoutes';
import { getUserCurrency, formatCurrencyExplicit } from '@/utils/currencyUtils';
import type { UserNotificationDTO } from '@/types';

export function useUserNotifications() {
  const { t } = useI18n();

  /**
   * Get the translated title for a notification
   * @param notification - The notification DTO
   * @returns Translated notification title
   */
  const getNotificationTitle = (notification: UserNotificationDTO): string => {
    const key = `notifications.types.${notification.type}.title`;
    return t(key);
  };

  /**
   * Get the translated message for a notification with context interpolation
   * @param notification - The notification DTO
   * @returns Translated notification message with interpolated context data
   */
  const getNotificationMessage = (notification: UserNotificationDTO): string => {
    const key = `notifications.types.${notification.type}.message`;
    const contextData = notification.contextData || {};
    
    // Format amount with currency if present
    const formattedContextData = { ...contextData };
    if (contextData.amount) {
      try {
        const amount = parseFloat(contextData.amount as string);
        if (!isNaN(amount)) {
          const userCurrency = getUserCurrency();
          formattedContextData.amount = formatCurrencyExplicit(amount, userCurrency, 'fr-FR');
        }
      } catch (error) {
        console.error('[Notification] Error formatting currency:', error);
        // Keep original amount if formatting fails
      }
    }
    
    return t(key, formattedContextData);
  };

  /**
   * Get the action URL for a notification
   * Frontend constructs URLs based on notification type and context
   * @param notification - The notification DTO
   * @returns Frontend route to navigate to
   */
  const getNotificationActionUrl = (notification: UserNotificationDTO): string => {
    return getNotificationRoute(notification);
  };

  /**
   * Format notification timestamp for display
   * @param timestamp - The notification timestamp
   * @param locale - Optional locale (defaults to current i18n locale)
   * @returns Formatted timestamp string
   */
  const formatNotificationTime = (timestamp: string, locale?: string): string => {
    if (!timestamp) {
      return t('notifications.time.unknown');
    }
    
    const date = new Date(timestamp);
    
    // Check if date is invalid
    if (isNaN(date.getTime())) {
      return t('notifications.time.unknown');
    }
    
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    // Less than 1 minute
    if (diffInSeconds < 60) {
      return t('notifications.time.justNow');
    }

    // Less than 1 hour
    if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return t('notifications.time.minutesAgo', { count: minutes });
    }

    // Less than 24 hours
    if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return t('notifications.time.hoursAgo', { count: hours });
    }

    // Less than 7 days
    if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return t('notifications.time.daysAgo', { count: days });
    }

    // More than 7 days - show formatted date
    try {
      return new Intl.DateTimeFormat(locale || 'en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    } catch (error) {
      return t('notifications.time.unknown');
    }
  };

  /**
   * Check if a notification is recent (less than 24 hours old)
   * @param notification - The notification DTO
   * @returns True if notification is less than 24 hours old
   */
  const isRecentNotification = (notification: UserNotificationDTO): boolean => {
    const notificationDate = new Date(notification.createdAt);
    const now = new Date();
    const diffInHours = (now.getTime() - notificationDate.getTime()) / (1000 * 60 * 60);
    return diffInHours < 24;
  };

  /**
   * Get notification icon based on type
   * @param notification - The notification or notification type string
   * @returns Icon class or name
   */
  const getNotificationIcon = (notification: UserNotificationDTO | string): string => {
    const notificationType = typeof notification === 'string' ? notification : notification.type;
    
    const iconMap: Record<string, string> = {
      PROVISIONING_REQUEST_NEW: 'fa-money-bill-wave',
      PROVISIONING_REQUEST_APPROVED: 'fa-check-circle',
      PROVISIONING_REQUEST_REJECTED: 'fa-times-circle',
      PROJECT_INVITATION: 'fa-envelope',
      PROJECT_CREATED: 'fa-folder-plus',
      PROJECT_STATUS_CHANGED: 'fa-sync',
      PROJECT_DEADLINE_APPROACHING: 'fa-clock',
      TASK_ASSIGNMENT: 'fa-tasks',
      TASK_COMPLETED: 'fa-check-square',
      TASK_OVERDUE: 'fa-exclamation-triangle',
      TASK_STATUS_CHANGED: 'fa-edit',
      PAYMENT_RECEIVED: 'fa-credit-card',
      PAYMENT_DUE: 'fa-calendar-alt',
      PAYMENT_OVERDUE: 'fa-exclamation-circle',
      DOCUMENT_SHARED: 'fa-file-alt',
      DOCUMENT_APPROVAL_REQUIRED: 'fa-clipboard-check',
      DOCUMENT_APPROVED: 'fa-thumbs-up',
      DOCUMENT_REJECTED: 'fa-thumbs-down',
      PHASE_STATUS_CHANGED: 'fa-layer-group',
      PHASE_COMPLETED: 'fa-flag-checkered',
      PHASE_DEADLINE_APPROACHING: 'fa-hourglass-half',
      SYSTEM_ANNOUNCEMENT: 'fa-bullhorn',
      SYSTEM_MAINTENANCE: 'fa-tools',
      SUBSCRIPTION_RENEWAL_ORDER: 'fa-file-invoice-dollar',
      SUBSCRIPTION_RENEWAL_SUCCESS: 'fa-check-circle',
      SUBSCRIPTION_EXPIRING_SOON: 'fa-exclamation-triangle',
    };

    return iconMap[notificationType] || 'fa-bell';
  };

  /**
   * Get notification color/variant based on type
   * @param notification - The notification or notification type string
   * @returns Color variant (success, warning, error, info)
   */
  const getNotificationVariant = (notification: UserNotificationDTO | string): string => {
    const notificationType = typeof notification === 'string' ? notification : notification.type;
    
    if (notificationType.includes('APPROVED') || notificationType.includes('COMPLETED') || notificationType.includes('RECEIVED') || notificationType.includes('SUCCESS')) {
      return 'success';
    }
    if (notificationType.includes('REJECTED') || notificationType.includes('OVERDUE')) {
      return 'error';
    }
    if (notificationType.includes('APPROACHING') || notificationType.includes('DUE') || notificationType.includes('REQUIRED') || notificationType.includes('EXPIRING') || notificationType.includes('ORDER')) {
      return 'warning';
    }
    return 'info';
  };

  return {
    getNotificationTitle,
    getNotificationMessage,
    getNotificationActionUrl,
    formatNotificationTime,
    isRecentNotification,
    getNotificationIcon,
    getNotificationVariant,
  };
}
